import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { formatTime, SALON_TZ } from '../lib/time';
import { SidePanel } from './forms';
import { Button, ErrorNote } from './ui';

type Review = {
  visits: Array<{ id: string; version: number; startsAt: string; name: string | null; services: string[]; noShow: boolean; canEdit: boolean }>;
  stillToCome: number;
  withoutNext: Array<{ customerId: string; name: string; services: string[] }>;
  tomorrow: { count: number; first: string | null; alerts: Array<{ name: string | null; startsAt: string }>; newClients: Array<{ name: string | null; startsAt: string }> };
};

/** Remembers on this device that today was closed, so Vandaag shows "Dag afgesloten" instead of the button. */
export const closedKey = (orgId: string, date: string) => `day-closed:${orgId}:${date}`;

/** "Dag afsluiten": no-shows, next appointments and a look at tomorrow, in one short panel. */
export function CloseDay({ orgId, locationId, date, onClose }: { orgId: string; locationId: string; date: string; onClose: (closed: boolean) => void }) {
  const t = useT();
  const qc = useQueryClient();
  const key = ['close-day', orgId, locationId, date];
  const review = useQuery({ queryKey: key, queryFn: () => api<Review>(`/orgs/${orgId}/close-day?${new URLSearchParams({ locationId, date })}`) });
  const noShow = useMutation({
    mutationFn: (v: { id: string; version: number }) => api(`/orgs/${orgId}/appointments/${v.id}/status`, { method: 'POST', body: JSON.stringify({ action: 'no_show', version: v.version }) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['calendar', orgId] });
    },
  });
  const r = review.data;
  const time = (iso: string) => formatTime(iso, SALON_TZ);
  const finish = () => {
    try {
      localStorage.setItem(closedKey(orgId, date), '1');
    } catch {
      // storage unavailable: the panel still closes
    }
    onClose(true);
  };

  return (
    <SidePanel open title={t('close.title')} onClose={() => onClose(false)} footer={<Button onClick={finish}>{t('close.finish')}</Button>}>
      {!r ? (
        <ErrorNote message={review.error instanceof ApiError ? review.error.message : review.error ? t('error.generic') : null} />
      ) : (
        <div className="flex flex-col gap-6">
          {r.stillToCome > 0 && <p className="rounded-[var(--radius-control)] bg-subtle px-3 py-2 text-sm">{t('close.stillToCome', { n: r.stillToCome })}</p>}

          <section className="flex flex-col gap-2">
            <h3 className="eyebrow">{t('close.noShows')}</h3>
            {r.visits.length === 0 ? (
              <p className="text-sm text-muted">{t('close.noVisits')}</p>
            ) : (
              <>
                <p className="-mt-1 text-sm text-muted">{t('close.noShowsHint')}</p>
                <ul className="divide-y divide-line overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface">
                  {r.visits.map((v) => (
                    <li key={v.id} className="flex items-center gap-3 px-3 py-2.5">
                      <span className="w-12 shrink-0 text-sm font-semibold tabular-nums">{time(v.startsAt)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{v.name ?? '—'}</span>
                        <span className="block truncate text-[13px] text-muted">{v.services.join(' + ')}</span>
                      </span>
                      {v.noShow ? (
                        <span className="tag bg-danger-soft text-danger">{t('status.no_show')}</span>
                      ) : (
                        v.canEdit && (
                          <button type="button" disabled={noShow.isPending} onClick={() => noShow.mutate(v)} className="shrink-0 rounded-full border border-line px-3 py-1 text-sm font-medium hover:bg-danger-soft hover:text-danger">
                            {t('close.markNoShow')}
                          </button>
                        )
                      )}
                    </li>
                  ))}
                </ul>
                <ErrorNote message={noShow.error instanceof ApiError ? noShow.error.message : noShow.error ? t('error.generic') : null} />
              </>
            )}
          </section>

          {r.visits.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="eyebrow">{t('close.withoutNext')}</h3>
              {r.withoutNext.length === 0 ? (
                <p className="text-sm text-muted">{t('close.allPlanned')}</p>
              ) : (
                <>
                  <p className="-mt-1 text-sm text-muted">{t('close.withoutNextHint')}</p>
                  <ul className="divide-y divide-line overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface">
                    {r.withoutNext.map((w) => (
                      <li key={w.customerId} className="flex items-center gap-3 px-3 py-2.5">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{w.name}</span>
                          <span className="block truncate text-[13px] text-muted">{w.services.join(' + ')}</span>
                        </span>
                        <Link to="/s/$orgId/agenda" params={{ orgId }} search={{ nieuw: 1, klant: w.customerId }} className="shrink-0 rounded-full bg-ink px-3 py-1 text-sm font-medium text-canvas">
                          {t('close.planNext')}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}

          <section className="flex flex-col gap-1.5">
            <h3 className="eyebrow">{t('close.tomorrow')}</h3>
            <p className="display text-[24px] leading-tight">
              {r.tomorrow.count === 0 ? t('close.tomorrowNone') : r.tomorrow.count === 1 ? t('close.tomorrowOne', { time: time(r.tomorrow.first!) }) : t('close.tomorrowCount', { n: r.tomorrow.count, time: time(r.tomorrow.first!) })}
            </p>
            {r.tomorrow.alerts.map((a, i) => (
              <p key={`a${i}`} className="text-sm text-warning">⚠ {t('close.alert', { name: a.name ?? '—', time: time(a.startsAt) })}</p>
            ))}
            {r.tomorrow.newClients.map((a, i) => (
              <p key={`n${i}`} className="text-sm text-muted">{t('close.newClient', { name: a.name ?? '—', time: time(a.startsAt) })}</p>
            ))}
          </section>
        </div>
      )}
    </SidePanel>
  );
}
